/**
 * Shared byte -> text decoding for every spreadsheet / CSV import path (#62, #64).
 *
 * SheetJS decodes a byte buffer of CSV text with no byte-order mark as Latin-1, so
 * UTF-8 files (Mac / Linux / Google Sheets) and Excel "ANSI" files from Hebrew /
 * Arabic Windows (windows-1255 / windows-1256) come out as mojibake in headers and
 * cells. For buffers that are plain text we decode the bytes ourselves and hand
 * SheetJS a string. XLSX zips, XLS OLE files and XML/HTML are returned untouched.
 */

const toBytes = (data: unknown): Uint8Array | undefined => {
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return undefined;
};

/**
 * Pick the legacy single-byte code page for a buffer that is NOT valid UTF-8.
 * The cp1252 accent ranges overlap the Hebrew / Arabic letter ranges, so byte VALUES alone
 * cannot tell them apart; byte RUNS can: whole Hebrew / Arabic words are 3+ consecutive high
 * bytes, while Latin text has isolated accents or pairs (ção, ße, ñó). Only a run >= 3
 * qualifies as RTL. Then cp1256 is chosen when Arabic-only letters appear (0xC1-0xDF is vowel
 * points in cp1255 — near absent from contact data — and Persian/Urdu 0x81/0x8D/0x8E/0x90),
 * else cp1255.
 */
export function detectAnsiCodePage(bytes: Uint8Array): 'windows-1255' | 'windows-1256' | 'windows-1252' {
    let run = 0;
    let maxRun = 0;
    let high = 0;
    let arabicOnly = 0;
    for (let i = 0; i < bytes.length; i++) {
        const v = bytes[i];
        if (v < 0x80) {
            run = 0;
            continue;
        }
        high++;
        run++;
        if (run > maxRun) maxRun = run;
        if ((v >= 0xc1 && v <= 0xdf) || v === 0x81 || v === 0x8d || v === 0x8e || v === 0x90) arabicOnly++;
    }
    if (maxRun < 3) return 'windows-1252';
    return arabicOnly / high >= 0.05 ? 'windows-1256' : 'windows-1255';
}

// windows-1252 differs from Latin-1 only in 0x80-0x9F. Decoded by table rather
// than TextDecoder('windows-1252') so behaviour is identical in every runtime
// (some ICU-less Node builds return '' for that label).
const CP1252_80_9F = '\u20ac\u0081\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u008d\u017d\u008f\u0090\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\u009d\u017e\u0178';
function decodeWindows1252(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const x = bytes[i];
        out += x >= 0x80 && x <= 0x9f ? CP1252_80_9F[x - 0x80] : String.fromCharCode(x);
    }
    return out;
}

/** Legacy ANSI decode: Hebrew / Arabic code pages via TextDecoder, else the table-based cp1252. */
function decodeAnsi(bytes: Uint8Array): string {
    const page = detectAnsiCodePage(bytes);
    if (page !== 'windows-1252') {
        try {
            return new TextDecoder(page).decode(bytes);
        } catch {
            // runtime without the legacy label (ICU-less) — fall through to cp1252
        }
    }
    return decodeWindows1252(bytes);
}

export function decodeCsvBytes(data: unknown): unknown {
    const bytes = toBytes(data);
    if (!bytes || bytes.length < 2 || typeof TextDecoder === 'undefined') return data;
    const [a, b, c, d] = bytes;
    if ((a === 0x50 && b === 0x4b && (c === 0x03 || c === 0x05 || c === 0x07) && (d === 0x04 || d === 0x06 || d === 0x08)) || (a === 0xd0 && b === 0xcf && c === 0x11 && d === 0xe0)) return data; // xlsx zip / xls OLE
    if (a === 0xff && b === 0xfe) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
    if (a === 0xfe && b === 0xff) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
    const text = (a === 0xef && b === 0xbb && c === 0xbf) ? bytes.subarray(3) : bytes;
    if (text[0] === 0x3c || bytes.includes(0)) return data; // '<' XML/HTML (SpreadsheetML), or NULs = binary
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(text);
    } catch {
        return decodeAnsi(text);
    }
}

interface XlsxReader {
    read: (data: any, opts?: any) => any;
}

/** XLSX.read that first routes text-like buffers through {@link decodeCsvBytes}. */
export function readSpreadsheet(XLSX: XlsxReader, data: unknown, opts: Record<string, unknown> = {}): any {
    const decoded = decodeCsvBytes(data);
    return typeof decoded === 'string'
        ? XLSX.read(decoded, { ...opts, type: 'string' })
        : XLSX.read(data, { type: 'array', ...opts });
}
